import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bot, BookOpen, Globe, Smartphone, Database,
  ArrowRight, CheckCircle, Code, ChevronRight, Zap, MessageSquare, Copy, Check,
} from 'lucide-react';

function CodeBlock({ children, language = '' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = typeof children === 'string' ? children : children?.toString?.() ?? '';
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-xl overflow-hidden border border-gray-700 my-4">
      <div className="bg-gray-800 px-4 py-1.5 flex items-center justify-between border-b border-gray-700">
        <span className="text-xs text-gray-400 font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
      <pre className="bg-gray-900 text-green-300 text-sm font-mono p-5 overflow-x-auto leading-relaxed whitespace-pre">
        {children}
      </pre>
    </div>
  );
}

function Step({ n, text }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center mt-0.5">
        {n}
      </span>
      <p className="text-gray-700">{text}</p>
    </div>
  );
}

function SectionHeading({ icon: Icon, id, title, subtitle }) {
  return (
    <div id={id} className="flex items-start gap-4 mb-6 pt-4">
      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-primary-600" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

const ChatbotHelpPage = () => {
  const { t } = useTranslation();

  const toc = [
    { id: 'create',         label: t('help.chatbot.toc_create') },
    { id: 'knowledge-base', label: t('help.chatbot.toc_kb') },
    { id: 'website',        label: t('help.chatbot.toc_website') },
    { id: 'mobile',         label: t('help.chatbot.toc_mobile') },
    { id: 'api',            label: t('help.chatbot.toc_api') },
    { id: 'whatsapp',       label: t('help.chatbot.toc_whatsapp') },
    { id: 'messenger',      label: t('help.chatbot.toc_messenger') },
    { id: 'instagram',      label: t('help.chatbot.toc_instagram') },
  ];

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Link to="/portal/help" className="hover:text-primary-600">{t('help.helpCenter')}</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium">{t('help.chatbot.title')}</span>
      </div>

      <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-8 border border-primary-100 mb-2">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-600 rounded-full mb-4">
          <Bot className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('help.chatbot.title')}</h1>
        <p className="text-gray-600 max-w-2xl">{t('help.chatbot.subtitle')}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sticky ToC */}
        <aside className="lg:w-52 flex-shrink-0">
          <div className="sticky top-6 bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">{t('help.onThisPage')}</p>
            <ul className="space-y-2">
              {toc.map(item => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="text-sm text-gray-600 hover:text-primary-600 flex items-center gap-1.5"
                  >
                    <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 max-w-3xl space-y-16 bg-white rounded-2xl border border-gray-200 p-8">

          {/* ── 1. Creating a Chatbot ─────────────────────────────────── */}
          <section>
            <SectionHeading icon={Bot} id="create"
              title={t('help.chatbot.s1_title')} subtitle={t('help.chatbot.s1_subtitle')} />
            <div className="space-y-1 mb-6">
              <Step n={1} text={t('help.chatbot.s1_step1')} />
              <Step n={2} text={t('help.chatbot.s1_step2')} />
              <Step n={3} text={t('help.chatbot.s1_step3')} />
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="pb-2 font-semibold">{t('help.field')}</th>
                    <th className="pb-2 font-semibold">{t('help.tableDescription')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    [t('help.chatbot.s1_fname'),    t('help.chatbot.s1_fnameDesc')],
                    [t('help.chatbot.s1_fBotName'), t('help.chatbot.s1_fBotNameDesc')],
                    [t('help.chatbot.s1_fWelcome'), t('help.chatbot.s1_fWelcomeDesc')],
                    [t('help.chatbot.s1_fPrompt'),  t('help.chatbot.s1_fPromptDesc')],
                    [t('help.chatbot.s1_fColor'),   t('help.chatbot.s1_fColorDesc')],
                    [t('help.chatbot.s1_fPosition'),t('help.chatbot.s1_fPositionDesc')],
                  ].map(([f, d]) => (
                    <tr key={f} className="text-gray-700">
                      <td className="py-2 pr-4 font-medium font-mono text-primary-700">{f}</td>
                      <td className="py-2">{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-800">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
              <span>{t('help.chatbot.s1_note')}</span>
            </div>
          </section>

          {/* ── 2. Knowledge Base & RAG ───────────────────────────────── */}
          <section>
            <SectionHeading icon={Database} id="knowledge-base"
              title={t('help.chatbot.s2_title')} subtitle={t('help.chatbot.s2_subtitle')} />
            <p className="text-gray-600 mb-5">{t('help.chatbot.s2_intro')}</p>
            <div className="space-y-5">
              <div className="rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-primary-600" /> {t('help.chatbot.s2_docs')}
                </h3>
                <p className="text-sm text-gray-600 mb-3">{t('help.chatbot.s2_docsDesc')}</p>
                <div className="flex items-start gap-2 text-xs text-gray-500">
                  <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-500 flex-shrink-0" />
                  {t('help.chatbot.s2_docsSupported')}
                </div>
              </div>
              <div className="rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary-600" /> {t('help.chatbot.s2_urls')}
                </h3>
                <p className="text-sm text-gray-600">{t('help.chatbot.s2_urlsDesc')}</p>
              </div>
              <div className="rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Code className="w-4 h-4 text-primary-600" /> {t('help.chatbot.s2_apis')}
                </h3>
                <p className="text-sm text-gray-600 mb-3">{t('help.chatbot.s2_apisDesc')}</p>
                <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1.5">
                  {[
                    [t('help.chatbot.s2_afName'),     t('help.chatbot.s2_afNameDesc')],
                    [t('help.chatbot.s2_afDesc'),     t('help.chatbot.s2_afDescDesc')],
                    [t('help.chatbot.s2_afEndpoint'), t('help.chatbot.s2_afEndpointDesc')],
                    [t('help.chatbot.s2_afAuthName'), t('help.chatbot.s2_afAuthNameDesc')],
                    [t('help.chatbot.s2_afAuthValue'),t('help.chatbot.s2_afAuthValueDesc')],
                  ].map(([f, d]) => (
                    <div key={f} className="flex gap-2">
                      <span className="font-mono text-primary-700 font-medium w-40 flex-shrink-0 text-xs">{f}</span>
                      <span className="text-gray-600 text-xs">{d}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 mt-3">
                  <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
                  {t('help.chatbot.s2_note')}
                </div>
              </div>
            </div>
          </section>

          {/* ── 3. Website Integration ────────────────────────────────── */}
          <section>
            <SectionHeading icon={Globe} id="website"
              title={t('help.chatbot.s3_title')} subtitle={t('help.chatbot.s3_subtitle')} />
            <div className="space-y-1 mb-4">
              <Step n={1} text={t('help.chatbot.s3_step1')} />
              <Step n={2} text={t('help.chatbot.s3_step2')} />
              <Step n={3} text={t('help.chatbot.s3_step3')} />
            </div>
            <CodeBlock language="html">{`<!-- Paste before </body> in your HTML page -->
<script
  src="https://your-platform.com/widget.js"
  data-chatbot-id="YOUR_CHATBOT_ID"
  data-api-url="https://your-platform.com"
  data-primary-color="#007bff"
  data-position="bottom-right"
  data-welcome-message="Hello! How can I help you?"
  data-bot-name="Customer Support"
></script>`}</CodeBlock>
          </section>

          {/* ── 4. Mobile App Integration ─────────────────────────────── */}
          <section>
            <SectionHeading icon={Smartphone} id="mobile"
              title={t('help.chatbot.s4_title')} subtitle={t('help.chatbot.s4_subtitle')} />
            <div className="space-y-1 mb-4">
              <Step n={1} text={t('help.chatbot.s4_step1')} />
            </div>
            <CodeBlock language="bash">{`npx expo install react-native-webview`}</CodeBlock>
            <div className="space-y-1 my-4">
              <Step n={2} text={t('help.chatbot.s4_step2')} />
            </div>
            <CodeBlock language="javascript">{`import React, { useState } from 'react';
import { View, TouchableOpacity, Text, Modal, SafeAreaView } from 'react-native';
import { WebView } from 'react-native-webview';

const CHATBOT_BASE = 'https://your-platform.com';

const WIDGET_HTML = \`
<!DOCTYPE html><html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
  <style>* { margin:0; padding:0; box-sizing:border-box; } body { background:#f9fafb; height:100vh; }</style>
</head>
<body>
  <script src="\${CHATBOT_BASE}/widget.js"
    data-chatbot-id="YOUR_CHATBOT_ID"
    data-api-url="\${CHATBOT_BASE}"
    data-primary-color="#007bff"
    data-position="bottom-right"
    data-welcome-message="Hello! How can I help you?"
    data-bot-name="Customer Support"
  ><\\/script>
</body></html>\`;

export default function ChatbotWidget() {
  const [visible, setVisible] = useState(false);
  return (
    <>
      <TouchableOpacity onPress={() => setVisible(true)}
        style={{ position:'absolute', bottom:80, right:20, width:56, height:56,
          borderRadius:28, backgroundColor:'#007bff', alignItems:'center',
          justifyContent:'center', elevation:8, zIndex:999 }}>
        <Text style={{ fontSize:24 }}>💬</Text>
      </TouchableOpacity>
      <Modal visible={visible} animationType="slide" onRequestClose={() => setVisible(false)}>
        <SafeAreaView style={{ flex:1 }}>
          <TouchableOpacity onPress={() => setVisible(false)}
            style={{ position:'absolute', top:12, right:12, zIndex:999,
              width:32, height:32, borderRadius:16, backgroundColor:'rgba(0,0,0,0.35)',
              alignItems:'center', justifyContent:'center' }}>
            <Text style={{ color:'#fff', fontSize:15, fontWeight:'700' }}>✕</Text>
          </TouchableOpacity>
          <WebView source={{ html:WIDGET_HTML, baseUrl:CHATBOT_BASE }}
            style={{ flex:1 }} javaScriptEnabled domStorageEnabled originWhitelist={['*']} />
        </SafeAreaView>
      </Modal>
    </>
  );
}`}</CodeBlock>
          </section>

          {/* ── 5. REST API Integration ───────────────────────────────── */}
          <section>
            <SectionHeading icon={Zap} id="api"
              title={t('help.chatbot.s5_title')} subtitle={t('help.chatbot.s5_subtitle')} />

            <p className="text-gray-600 mb-5">{t('help.chatbot.s5_intro')}</p>

            <h3 className="font-semibold text-gray-900 mb-2">{t('help.chatbot.s5_sendMsg')}</h3>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-3 text-sm font-mono">
              <span className="text-green-700 font-semibold">POST</span>{' '}
              <span className="text-gray-800">https://your-platform.com/api/chat</span>
            </div>

            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('help.chatbot.s5_reqFields')}</p>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                  {[
                    ['sessionId',       t('help.required'), t('help.chatbot.s5_fSessionIdDesc')],
                    ['message',         t('help.required'), t('help.chatbot.s5_fMessageDesc')],
                    ['visitorId',       t('help.optional'), t('help.chatbot.s5_fVisitorIdDesc')],
                    ['visitorEmail',    t('help.optional'), t('help.chatbot.s5_fVisitorEmailDesc')],
                    ['visitorName',     t('help.optional'), t('help.chatbot.s5_fVisitorNameDesc')],
                    ['mediaUrl',        t('help.optional'), t('help.chatbot.s5_fMediaUrlDesc')],
                    ['channelType',     t('help.optional'), t('help.chatbot.s5_fChannelTypeDesc')],
                    ['channelSenderId', t('help.optional'), t('help.chatbot.s5_fChannelSenderIdDesc')],
                  ].map(([f, r, d]) => (
                    <tr key={f}>
                      <td className="py-1.5 pr-2 font-mono text-primary-700 w-36">{f}</td>
                      <td className="py-1.5 pr-2 w-24">{r}</td>
                      <td className="py-1.5 text-gray-600">{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <CodeBlock language="bash">{`# cURL — Widget Key (for embedded widget)
curl -X POST https://your-platform.com/api/chat \\
  -H "X-Widget-Key: YOUR_WIDGET_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "sessionId": "session-abc123",
    "message":   "What products do you offer?",
    "visitorId": "user-42"
  }'

# cURL — API Key (for server-side custom integrations)
curl -X POST https://your-platform.com/api/chat \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "sessionId": "session-abc123",
    "message":   "What products do you offer?",
    "visitorId": "user-42"
  }'`}</CodeBlock>

            <CodeBlock language="javascript">{`// JavaScript (fetch) — use Widget Key for browser widgets
const res = await fetch('https://your-platform.com/api/chat', {
  method: 'POST',
  headers: {
    'X-Widget-Key': 'YOUR_WIDGET_KEY',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    sessionId: 'session-abc123',
    message:   'What products do you offer?',
    visitorId: 'user-42',
  }),
});
const data = await res.json();
console.log(data.message); // AI reply text

// Server-side: use API Key (generated in chatbot settings)
const res = await fetch('https://your-platform.com/api/chat', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    sessionId: 'session-abc123',
    message:   'What products do you offer?',
    visitorId: 'user-42',
  }),
});`}</CodeBlock>

            <CodeBlock language="python">{`# Python (requests) — server-side using API Key
import requests, uuid

res = requests.post(
    'https://your-platform.com/api/chat',
    headers={
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json',
    },
    json={
        'sessionId': str(uuid.uuid4()),
        'message':   'What products do you offer?',
        'visitorId': 'user-42',
    }
)
print(res.json()['message'])`}</CodeBlock>

            <h3 className="font-semibold text-gray-900 mb-2 mt-4">{t('help.chatbot.s5_response')}</h3>
            <CodeBlock language="json">{`{
  "sessionId": "session-abc123",
  "message":   "We offer two products: a CMMS and an AI Chatbot platform.",
  "timestamp": "2025-06-01T12:00:00Z"
}`}</CodeBlock>

            <h3 className="font-semibold text-gray-900 mb-2 mt-6">{t('help.chatbot.s5_endConv')}</h3>
            <CodeBlock language="bash">{`curl -X POST https://your-platform.com/api/chat/session-abc123/end`}</CodeBlock>

            <div className="flex items-start gap-2 p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-800 mt-4">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <span>{t('help.chatbot.s5_note')}</span>
            </div>
          </section>

          {/* ── 6. WhatsApp Channel Setup ─────────────────────────────── */}
          <section>
            <SectionHeading icon={MessageSquare} id="whatsapp"
              title={t('help.chatbot.s6_title')} subtitle={t('help.chatbot.s6_subtitle')} />

            <p className="text-gray-600 mb-5">{t('help.chatbot.s6_intro')}</p>

            <h3 className="font-semibold text-gray-900 mb-3 text-base">{t('help.prerequisites')}</h3>
            <div className="rounded-xl border border-gray-200 p-5 mb-5 space-y-2">
              {[
                [t('help.chatbot.s6_p1'), t('help.chatbot.s6_p1Desc')],
                [t('help.chatbot.s6_p2'), t('help.chatbot.s6_p2Desc')],
                [t('help.chatbot.s6_p3'), t('help.chatbot.s6_p3Desc')],
                [t('help.chatbot.s6_p4'), t('help.chatbot.s6_p4Desc')],
              ].map(([title, desc]) => (
                <div key={title} className="flex gap-3 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <div><span className="font-semibold text-gray-800">{title}:</span> <span className="text-gray-600">{desc}</span></div>
                </div>
              ))}
            </div>

            <h3 className="font-semibold text-gray-900 mb-3 text-base">{t('help.chatbot.s6_step1Title')}</h3>
            <div className="space-y-1 mb-4">
              <Step n={1} text={t('help.chatbot.s6_step1_1')} />
              <Step n={2} text={t('help.chatbot.s6_step1_2')} />
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-4 text-xs">
              {[
                [t('help.chatbot.s6_fDispName'),    t('help.chatbot.s6_fDispNameDesc')],
                [t('help.chatbot.s6_fPhoneId'),     t('help.chatbot.s6_fPhoneIdDesc')],
                [t('help.chatbot.s6_fToken'),       t('help.chatbot.s6_fTokenDesc')],
                [t('help.chatbot.s6_fWebhookToken'),t('help.chatbot.s6_fWebhookTokenDesc')],
              ].map(([f, d]) => (
                <div key={f} className="flex gap-2 py-1 border-b border-gray-100 last:border-0">
                  <span className="font-mono text-primary-700 font-medium w-44 flex-shrink-0">{f}</span>
                  <span className="text-gray-600">{d}</span>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-800 mb-5">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
              <span>{t('help.chatbot.s6_step1Note')}</span>
            </div>

            <h3 className="font-semibold text-gray-900 mb-3 text-base">{t('help.chatbot.s6_step2Title')}</h3>
            <div className="space-y-1 mb-5">
              <Step n={1} text={t('help.chatbot.s6_step2_1')} />
              <Step n={2} text={t('help.chatbot.s6_step2_2')} />
              <Step n={3} text={t('help.chatbot.s6_step2_3')} />
              <Step n={4} text={t('help.chatbot.s6_step2_4')} />
              <Step n={5} text={t('help.chatbot.s6_step2_5')} />
            </div>

            <h3 className="font-semibold text-gray-900 mb-3 text-base">{t('help.chatbot.s6_howTitle')}</h3>
            <div className="rounded-xl border border-gray-200 p-5 mb-5 space-y-2 text-sm text-gray-600">
              {[
                t('help.chatbot.s6_how1'),
                t('help.chatbot.s6_how2'),
                t('help.chatbot.s6_how3'),
                t('help.chatbot.s6_how4'),
              ].map((text, i) => (
                <div key={i} className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <p>{text}</p>
                </div>
              ))}
            </div>

            <div className="flex items-start gap-2 p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-800">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <span>{t('help.chatbot.s6_billing')}</span>
            </div>
          </section>

          {/* ── Messenger Channel Setup ─────────────────────────── */}
          <section id="messenger" className="scroll-mt-4">
            <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              {t('help.chatbot.s7_title')}
            </h2>
            <p className="text-sm text-gray-500 mb-4">{t('help.chatbot.s7_subtitle')}</p>

            <p className="text-gray-600 text-sm mb-4">{t('help.chatbot.s7_intro')}</p>

            <h3 className="font-semibold text-gray-800 mb-2 text-sm">{t('help.prerequisites')}</h3>
            <ul className="space-y-1 mb-4">
              {[
                t('help.chatbot.s7_p1'),
                t('help.chatbot.s7_p2'),
                t('help.chatbot.s7_p3'),
                t('help.chatbot.s7_p4'),
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>

            <h3 className="font-semibold text-gray-800 mb-2 text-sm">{t('help.chatbot.s7_step1Title')}</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 mb-4 pl-1">
              <li>{t('help.chatbot.s7_step1_1')}</li>
              <li>{t('help.chatbot.s7_step1_2')}</li>
              <li>{t('help.chatbot.s7_step1_3')}</li>
              <li>{t('help.chatbot.s7_step1_4')}</li>
            </ol>

            <h3 className="font-semibold text-gray-800 mb-2 text-sm">{t('help.chatbot.s7_step2Title')}</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 mb-4 pl-1">
              <li>{t('help.chatbot.s7_step2_1')}</li>
              <li>{t('help.chatbot.s7_step2_2')}</li>
              <li>{t('help.chatbot.s7_step2_3')}</li>
              <li>{t('help.chatbot.s7_step2_4')}</li>
            </ol>

            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <span>{t('help.chatbot.s7_billing')}</span>
            </div>
          </section>

          {/* ── Instagram Channel Setup ─────────────────────────── */}
          <section id="instagram" className="scroll-mt-4">
            <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-500" />
              {t('help.chatbot.s8_title')}
            </h2>
            <p className="text-sm text-gray-500 mb-4">{t('help.chatbot.s8_subtitle')}</p>

            <p className="text-gray-600 text-sm mb-4">{t('help.chatbot.s8_intro')}</p>

            <h3 className="font-semibold text-gray-800 mb-2 text-sm">{t('help.prerequisites')}</h3>
            <ul className="space-y-1 mb-4">
              {[
                t('help.chatbot.s8_p1'),
                t('help.chatbot.s8_p2'),
                t('help.chatbot.s8_p3'),
                t('help.chatbot.s8_p4'),
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>

            <h3 className="font-semibold text-gray-800 mb-2 text-sm">{t('help.chatbot.s8_step1Title')}</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 mb-4 pl-1">
              <li>{t('help.chatbot.s8_step1_1')}</li>
              <li>{t('help.chatbot.s8_step1_2')}</li>
              <li>{t('help.chatbot.s8_step1_3')}</li>
              <li>{t('help.chatbot.s8_step1_4')}</li>
            </ol>

            <h3 className="font-semibold text-gray-800 mb-2 text-sm">{t('help.chatbot.s8_step2Title')}</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 mb-4 pl-1">
              <li>{t('help.chatbot.s8_step2_1')}</li>
              <li>{t('help.chatbot.s8_step2_2')}</li>
              <li>{t('help.chatbot.s8_step2_3')}</li>
              <li>{t('help.chatbot.s8_step2_4')}</li>
            </ol>

            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <span>{t('help.chatbot.s8_note')}</span>
            </div>
          </section>

        </main>
      </div>

      <div className="flex gap-4">
        <Link to="/portal/help" className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
          {t('help.backToHelp')}
        </Link>
        <Link to="/portal/support" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium">
          {t('help.openSupport')}
        </Link>
      </div>
    </div>
  );
};

export default ChatbotHelpPage;
